import { useEffect, useState } from "react";
import { Search, X } from "lucide-react";
import { ADMIN_SECTIONS, sectionLabelNo } from "./sections";

const SLIDE_SHAPES = {
  batch: [["w-1/2", "h-1.5"], ["w-full", "h-3", "pill-row"]],
  zoom: [["w-2/3", "h-1.5"], ["w-full", "h-5", "box"]],
  export: [["w-1/3", "h-1.5"], ["w-full", "h-2", "dots"]],
  site: [["w-1/2", "h-1.5", "center"], ["w-1/3", "h-1", "center"], ["w-1/4", "h-4", "center", "photo"]],
  announcement: [["w-full", "h-3", "dark"], ["w-full", "h-1.5", "accent"], ["cols"]],
  social: [["w-1/2", "h-1.5"], ["w-full", "h-1"], ["w-full", "h-1"], ["w-full", "h-1"]],
  festival: [["w-1/3", "h-5", "photo"], ["w-1/2", "h-1.5"]],
  programs: [["w-full", "h-5", "card"], ["w-full", "h-5", "card"]],
  welcome: [["w-3/4", "h-1.5"], ["w-full", "h-6", "box"]],
  classroom: [["w-full", "h-6", "box"], ["w-1/2", "h-1"], ["w-1/2", "h-1"]],
  whatsapp: [["w-full", "h-4", "box"], ["w-2/3", "h-1"], ["w-2/3", "h-1"]],
  feedback: [["w-1/2", "h-1.5"], ["w-full", "h-2", "dots"], ["w-full", "h-1"], ["w-3/4", "h-1"]],
  testimonials: [["w-1/2", "h-1.5"], ["w-full", "h-4", "card"], ["w-full", "h-1"]],
  guides: [["w-1/2", "h-1.5"], ["grid"]],
  registrations: [["w-full", "h-1"], ["w-full", "h-1"], ["w-full", "h-1"], ["w-full", "h-1"], ["w-full", "h-1"]],
  past: [["w-1/2", "h-1.5"], ["w-full", "h-2", "dots"], ["w-full", "h-1"], ["w-full", "h-1"]],
};

const Shape = ({ spec }) => {
  const [w, h, kind] = spec;
  if (spec[0] === "cols") return <div className="flex gap-1 flex-1"><div className="flex-1 rounded-sm bg-[#1A2E26]/15" /><div className="flex-1 rounded-sm bg-[#1A2E26]/15" /></div>;
  if (spec[0] === "grid") return <div className="grid grid-cols-4 gap-0.5 flex-1">{Array.from({ length: 8 }).map((_, i) => <div key={i} className="rounded-sm bg-[#1A2E26]/20" />)}</div>;
  if (kind === "pill-row") return <div className="flex gap-0.5">{[0, 1, 2].map((i) => <div key={i} className="h-1.5 flex-1 rounded-full bg-[#1A2E26]/20" />)}</div>;
  if (kind === "dots") return <div className="flex gap-0.5">{[0, 1, 2, 3].map((i) => <div key={i} className="w-1.5 h-1.5 rounded-full bg-[#1A2E26]/25" />)}</div>;
  const color = kind === "dark" ? "bg-[#1A2E26]/70" : kind === "accent" ? "bg-[#C86A51]/50" : kind === "photo" ? "bg-[#C86A51]/30" : kind === "card" || kind === "box" ? "bg-white/80 border border-[#1A2E26]/15" : "bg-[#1A2E26]/20";
  return <div className={`${w} ${h} rounded-sm ${color} ${spec.includes("center") ? "mx-auto" : ""}`} />;
};

export const useActiveSection = () => {
  const [active, setActive] = useState(ADMIN_SECTIONS[0].id);
  useEffect(() => {
    const onScroll = () => {
      let current = ADMIN_SECTIONS[0].id;
      for (const s of ADMIN_SECTIONS) {
        const el = document.getElementById(`admin-section-${s.id}`);
        if (el && el.getBoundingClientRect().top <= 160) current = s.id;
      }
      setActive(current);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return active;
};

export const jumpTo = (id) => document.getElementById(`admin-section-${id}`)?.scrollIntoView({ behavior: "smooth", block: "start" });

const ZOOMS = [0.5, 0.6, 0.7, 0.8, 0.9, 1];
const useNavZoom = () => {
  const [z, setZ] = useState(() => { const v = Number(typeof window !== "undefined" && localStorage.getItem("pl-admin-nav-zoom")); return ZOOMS.includes(v) ? v : 1; });
  const step = (d) => setZ((cur) => { const n = ZOOMS[Math.min(ZOOMS.length - 1, Math.max(0, ZOOMS.indexOf(cur) + d))]; localStorage.setItem("pl-admin-nav-zoom", String(n)); return n; });
  return [z, step];
};

/* Zoom only the section thumbnails; the admin body on the right is untouched. */
const NavZoom = ({ z, step }) => (
  <div data-testid="slide-nav-zoom" className="flex items-center gap-1 rounded-full bg-white/80 border border-border/70 px-1 py-0.5">
    <button type="button" data-testid="slide-nav-zoom-out" onClick={() => step(-1)} disabled={z === ZOOMS[0]} title="Zoom out — see more sections" className="w-5 h-5 grid place-items-center rounded-full text-sm font-bold hover:bg-[#1A2E26] hover:text-white disabled:opacity-30">−</button>
    <span data-testid="slide-nav-zoom-value" className="text-[10px] tabular-nums w-8 text-center text-muted-foreground">{Math.round(z * 100)}%</span>
    <button type="button" data-testid="slide-nav-zoom-in" onClick={() => step(1)} disabled={z === ZOOMS[ZOOMS.length - 1]} title="Zoom in" className="w-5 h-5 grid place-items-center rounded-full text-sm font-bold hover:bg-[#1A2E26] hover:text-white disabled:opacity-30">+</button>
  </div>
);

export const AdminSidebar = () => {
  const active = useActiveSection();
  const [z, step] = useNavZoom();
  const [q, setQ] = useState("");
  const shown = q.trim() ? ADMIN_SECTIONS.filter((s) => `${sectionLabelNo(s.id)} ${s.title}`.toLowerCase().includes(q.trim().toLowerCase())) : ADMIN_SECTIONS;
  return (
    <>
      <aside data-testid="admin-slide-nav" className="hidden xl:flex fixed left-[calc(var(--rail-w)+8px)] top-6 bottom-6 w-52 flex-col gap-1.5 overflow-y-auto pr-1 z-40">
        <div className="flex items-center justify-between px-1"><p className="text-[10px] uppercase tracking-[0.3em] text-accent">Sections</p><NavZoom z={z} step={step} /></div>
        <div className="relative px-1">
          <Search size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input data-testid="slide-nav-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find a section…" className="w-full rounded-full border border-border/70 bg-white/80 pl-7 pr-7 py-1 text-[11px] focus:outline-none focus:ring-2 focus:ring-accent/40" />
          {q && <button type="button" data-testid="slide-nav-search-clear" onClick={() => setQ("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"><X size={12} /></button>}
        </div>
        <div data-testid="slide-nav-list" data-zoom={z} className="flex flex-col gap-1.5" style={{ zoom: z, width: "13rem" }}>
        {shown.length === 0 && <p data-testid="slide-nav-empty" className="px-2 py-3 text-[11px] text-muted-foreground italic">No section matches “{q}”</p>}
        {shown.map((s, i) => {
          const isActive = active === s.id;
          return (
            <button
              key={s.id}
              type="button"
              data-testid={`slide-nav-${s.id}`}
              onClick={() => jumpTo(s.id)}
              className={`group text-left rounded-xl p-1.5 transition-colors ${isActive ? "bg-[#1A2E26]/8" : "hover:bg-white/60"}`}
            >
              <div className="flex items-start gap-2">
                <span className={`text-[11px] font-semibold tabular-nums mt-0.5 w-7 shrink-0 ${isActive ? "text-accent" : "text-muted-foreground"}`}>{sectionLabelNo(s.id)}</span>
                <div className="flex-1 min-w-0">
                  <div
                    className={`aspect-[16/9] rounded-md border-2 p-1.5 flex flex-col gap-1 transition-[transform,border-color,box-shadow] duration-300 group-hover:-translate-y-0.5 ${isActive ? "border-accent shadow-[0_6px_18px_rgba(200,106,81,0.25)]" : "border-white/80 shadow-sm"}`}
                    style={{ backgroundColor: s.tint }}
                  >
                    {(SLIDE_SHAPES[s.id] || []).map((spec, j) => <Shape key={j} spec={spec} />)}
                  </div>
                  <p className={`mt-1 text-[22px] leading-tight break-words ${isActive ? "text-foreground font-medium" : "text-muted-foreground"}`}>{s.title}</p>
                </div>
              </div>
            </button>
          );
        })}
        </div>
        <div data-testid="slide-nav-arrows" className="sticky bottom-0 mt-1 flex gap-1.5 rounded-xl bg-[#EDECE3]/95 backdrop-blur p-1.5 border border-border/60">
          {[["prev", "▲ Prev slide", -1], ["next", "▼ Next slide", 1]].map(([k, l, d]) => {
            const idx = ADMIN_SECTIONS.findIndex((x) => x.id === active);
            const target = ADMIN_SECTIONS[idx + d];
            return <button key={k} type="button" data-testid={`slide-nav-${k}`} disabled={!target} onClick={() => target && jumpTo(target.id)} className="flex-1 rounded-lg bg-white border border-border px-2 py-1.5 text-[11px] font-medium hover:bg-[#1A2E26] hover:text-white disabled:opacity-40 transition-colors">{l}</button>;
          })}
        </div>
      </aside>

      <div data-testid="admin-slide-nav-mobile" className="xl:hidden sticky top-0 z-40 -mx-6 px-6 py-2 mb-6 bg-[#EDECE3]/90 backdrop-blur border-b border-border/60 flex gap-2 overflow-x-auto">
        {ADMIN_SECTIONS.map((s, i) => (
          <button
            key={s.id}
            type="button"
            data-testid={`slide-chip-${s.id}`}
            onClick={() => jumpTo(s.id)}
            className={`shrink-0 inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs transition-colors ${active === s.id ? "bg-primary text-primary-foreground border-primary" : "border-border bg-white/70 text-muted-foreground"}`}
          >
            <span className="font-semibold tabular-nums">{sectionLabelNo(s.id)}</span> {s.title}
          </button>
        ))}
      </div>
    </>
  );
};
